import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Typography, Box, Accordion, Card, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableHead, TableRow, TextField, Button, Chip, Alert } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { API_KEYS_URL } from '../links';

function TryIt({ apiKey, buildUrl, params, onChange, fields }) {
  const [state, setState] = useState({ loading: false, status: null, body: null, error: null });

  const run = async () => {
    if (!apiKey) {
      setState({ loading: false, status: null, body: null, error: 'Paste your API key above first.' });
      return;
    }
    setState({ loading: true, status: null, body: null, error: null });
    const url = buildUrl();
    try {
      const res = await fetch(url, { headers: { 'X-API-Key': apiKey } });
      const body = await res.json();
      setState({ loading: false, status: res.status, body, error: null });
    } catch (e) {
      setState({ loading: false, status: null, body: null, error: e.message });
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3, mb: 2 }}>Try It</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {fields.map((f) => (
          <TextField
            key={f.name}
            label={f.label}
            value={params[f.name] || ''}
            onChange={(e) => onChange({ ...params, [f.name]: e.target.value })}
            size="small"
            sx={{ width: f.width || 160 }}
          />
        ))}
        <Button variant="contained" onClick={run} disabled={state.loading}>
          {state.loading ? 'Running...' : 'Run against the live API'}
        </Button>
      </Box>
      <Typography variant="body2" sx={{ mb: 1 }}>Request:</Typography>
      <SyntaxHighlighter language="http" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px' }}>
        {`GET ${buildUrl()}\nX-API-Key: ${apiKey ? '••••••••' : 'your-api-key'}`}
      </SyntaxHighlighter>
      {state.error && <Alert severity="error" sx={{ mt: 1 }}>{state.error}</Alert>}
      {state.status !== null && (
        <>
          <Typography variant="body2" sx={{ mt: 1 }}>Response ({state.status}):</Typography>
          <SyntaxHighlighter language="json" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px' }}>
            {JSON.stringify(state.body, null, 2)}
          </SyntaxHighlighter>
        </>
      )}
    </Box>
  );
}

function ApiDocs() {
  const [apiKey, setApiKey] = useState('');
  const [keyCheck, setKeyCheck] = useState({ status: 'idle', message: '' });

  const handleAuthorize = async () => {
    if (!apiKey) {
      setKeyCheck({ status: 'invalid', message: 'Paste a key first.' });
      return;
    }
    setKeyCheck({ status: 'checking', message: '' });
    try {
      // /v1/reports/summary takes no params, so it's a cheap way to confirm
      // the key itself authenticates - not a check of every scope, just that
      // the key is real, active, and not revoked or paused.
      const res = await fetch('/v1/reports/summary', { headers: { 'X-API-Key': apiKey } });
      const limit = res.headers.get('x-ratelimit-limit');
      if (res.ok) {
        setKeyCheck({ status: 'valid', message: limit ? `Key is valid and active (${limit} requests/hour).` : 'Key is valid and active.' });
      } else {
        const body = await res.json().catch(() => ({}));
        setKeyCheck({ status: 'invalid', message: (body.error && body.error.message) || `Key check failed (HTTP ${res.status}).` });
      }
    } catch (e) {
      setKeyCheck({ status: 'invalid', message: 'Could not reach the API to check the key.' });
    }
  };

  const [granularParams, setGranularParams] = useState({ limit: '2', reportType: 'uxo', country: 'Ukraine', priority: '' });
  const [summaryParams] = useState({});
  const [usersParams, setUsersParams] = useState({ groupBy: 'country' });
  const [boloParams, setBoloParams] = useState({ limit: '2', countryCode: 'US' });

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.hash]);

  const buildGranularUrl = () => {
    const qp = [];
    if (granularParams.limit) qp.push(`limit=${encodeURIComponent(granularParams.limit)}`);
    if (granularParams.reportType) qp.push(`reportType=${encodeURIComponent(granularParams.reportType)}`);
    if (granularParams.country) qp.push(`country=${encodeURIComponent(granularParams.country)}`);
    if (granularParams.priority) qp.push(`priority=${encodeURIComponent(granularParams.priority)}`);
    return `/v1/reports/data${qp.length ? `?${qp.join('&')}` : ''}`;
  };
  const buildSummaryUrl = () => '/v1/reports/summary';
  const buildUsersUrl = () => {
    const qp = [];
    if (usersParams.groupBy) qp.push(`groupBy=${encodeURIComponent(usersParams.groupBy)}`);
    return `/v1/users/summary${qp.length ? `?${qp.join('&')}` : ''}`;
  };
  const buildBoloUrl = () => {
    const qp = [];
    if (boloParams.limit) qp.push(`limit=${encodeURIComponent(boloParams.limit)}`);
    if (boloParams.countryCode) qp.push(`countryCode=${encodeURIComponent(boloParams.countryCode)}`);
    return `/v1/bolo-reports/namus${qp.length ? `?${qp.join('&')}` : ''}`;
  };

  return (
    <Box>
      {/* Introduction */}
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
        API Documentation
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Welcome to the Arc Light Technologies API Documentation. This API provides access to real-time open-source intelligence data, including granular reports, missing-persons (BOLO) data, and aggregated analytics. All requests require an <code>X-API-Key</code> header for authentication.
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Full machine-readable spec: <a href="/openapi.yaml">openapi.yaml</a> / <a href="/openapi.json">openapi.json</a>
      </Typography>

      <Card sx={{ p: 2, mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Paste your API key here to run any "Try It" example below against the live API (never sent anywhere but directly to this API - not stored):
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            type="password"
            placeholder="your-api-key"
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); setKeyCheck({ status: 'idle', message: '' }); }}
          />
          <Button variant="contained" onClick={handleAuthorize} disabled={keyCheck.status === 'checking'} sx={{ flexShrink: 0 }}>
            {keyCheck.status === 'checking' ? 'Checking...' : 'Authorize'}
          </Button>
        </Box>
        {keyCheck.status === 'valid' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {keyCheck.message} Scroll to any endpoint below and click "Run against the live API" to try it.
          </Alert>
        )}
        {keyCheck.status === 'invalid' && (
          <Alert severity="error" sx={{ mt: 2 }}>{keyCheck.message}</Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: keyCheck.status === 'idle' ? 1 : 0 }}>
          Request a key or manage existing ones on your <a href={API_KEYS_URL} target="_blank" rel="noopener noreferrer">SleuthNet profile</a> (API tab).
        </Typography>
      </Card>

      {/* Authentication Section */}
      <Accordion defaultExpanded id="authentication">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Authentication</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            All API requests require an API key, which must be included in the <code>X-API-Key</code> header. API access requires an existing Professional, Business, or Enterprise account - you can request a key on your <a href={API_KEYS_URL} target="_blank" rel="noopener noreferrer">SleuthNet profile</a> (API tab), and requests are reviewed before a key is issued.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Each key carries a <strong>tier</strong> (Professional / Business / Enterprise, matching your account plan and controlling rate limits and quota - see below) and one or more <strong>scopes</strong> controlling which endpoints it can call:
          </Typography>
          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Scope</strong></TableCell>
                  <TableCell><strong>Grants access to</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell><code>reports:read</code></TableCell><TableCell><code>/reports/data</code>, <code>/reports/summary</code></TableCell></TableRow>
                <TableRow><TableCell><code>users:summary:read</code></TableCell><TableCell><code>/users/summary</code></TableCell></TableRow>
                <TableRow><TableCell><code>bolo:read</code></TableCell><TableCell><code>/bolo-reports/namus</code> (public fields only - see the BOLO section below)</TableCell></TableRow>
                <TableRow><TableCell><code>bolo:read:pii</code></TableCell><TableCell>Adds contact info, exact coordinates, and date of birth to BOLO responses. Granted manually, independent of tier - not part of the standard request flow.</TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>
          <Card sx={{ bgcolor: 'rgba(196,30,30,0.08)', border: '1px solid #c04545', p: 2, mb: 2 }}>
            <Typography variant="body1" sx={{ color: '#ef8080' }}>
              <strong>Important:</strong> Do not include your API key in the URL (e.g., <code>?apiKey=YOUR_API_KEY</code>) - always use the <code>X-API-Key</code> header. Keys should never appear in browser history, server logs, or shared URLs.
            </Typography>
          </Card>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>Example: Making a Request</Typography>
          <Typography variant="body1" sx={{ mb: 1, mt: 2 }}>Python (using requests)</Typography>
          <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px' }}>
            {`import requests

url = "https://api.sleuthnet.com/v1/reports/data?limit=2&reportType=uxo&country=Ukraine"
headers = {
    "X-API-Key": "your-api-key"
}

response = requests.get(url, headers=headers)
print(response.json())
`}
          </SyntaxHighlighter>
          <Typography variant="body1" sx={{ mb: 1, mt: 3, fontWeight: 'bold' }}>JavaScript (using fetch)</Typography>
          <SyntaxHighlighter language="javascript" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px' }}>
            {`fetch('https://api.sleuthnet.com/v1/reports/data?limit=2&reportType=uxo&country=Ukraine', {
  headers: {
    'X-API-Key': 'your-api-key'
  }
})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));
`}
          </SyntaxHighlighter>
        </AccordionDetails>
      </Accordion>

      {/* Versioning Section */}
      <Accordion defaultExpanded id="versioning">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Versioning</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1">
            All endpoints are available under <code>/v1/</code> (e.g. <code>/v1/reports/data</code>). The same endpoints are also reachable without the version prefix for backward compatibility (e.g. <code>/reports/data</code>), but new integrations should use <code>/v1/</code> paths.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Rate Limits Section */}
      <Accordion defaultExpanded id="rate-limits">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Rate Limits & Quotas</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Limits apply to your account (not per key or IP), so a new key keeps your usage. They follow your plan. The hourly limit counts every request and resets at the top of each hour (UTC); the monthly quota counts successful requests only and resets on the 1st of each month (UTC):
          </Typography>
          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Tier</strong></TableCell>
                  <TableCell><strong>Requests / hour</strong></TableCell>
                  <TableCell><strong>Requests / month</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell>Professional</TableCell><TableCell>100</TableCell><TableCell>2,000</TableCell></TableRow>
                <TableRow><TableCell>Business</TableCell><TableCell>1,000</TableCell><TableCell>50,000</TableCell></TableRow>
                <TableRow><TableCell>Enterprise</TableCell><TableCell>10,000</TableCell><TableCell>Custom / unlimited</TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Every authenticated response includes headers so your client can self-throttle instead of guessing:
          </Typography>
          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell><code>X-RateLimit-Limit</code></TableCell><TableCell>Hourly request limit</TableCell></TableRow>
                <TableRow><TableCell><code>X-RateLimit-Remaining</code></TableCell><TableCell>Requests remaining in the current hour</TableCell></TableRow>
                <TableRow><TableCell><code>X-RateLimit-Reset</code></TableCell><TableCell>Unix timestamp when the hourly window resets</TableCell></TableRow>
                <TableRow><TableCell><code>X-Quota-Limit</code> / <code>X-Quota-Remaining</code> / <code>X-Quota-Reset</code></TableCell><TableCell>Same, for the monthly quota</TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Exceeding the hourly limit or monthly quota returns <code>429</code> with a <code>Retry-After</code> header (seconds) and a distinguishing error code:
          </Typography>
          <SyntaxHighlighter language="json" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px' }}>
            {`{ "error": { "code": "RATE_LIMIT_EXCEEDED", "message": "Hourly rate limit exceeded" } }`}
          </SyntaxHighlighter>
          <SyntaxHighlighter language="json" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px', marginTop: '8px' }}>
            {`{ "error": { "code": "QUOTA_EXCEEDED", "message": "Monthly quota exceeded" } }`}
          </SyntaxHighlighter>
        </AccordionDetails>
      </Accordion>

      {/* Pagination Section */}
      <Accordion defaultExpanded id="pagination">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Pagination</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            List endpoints (<code>/reports/data</code>, <code>/bolo-reports/namus</code>) use opaque cursor pagination:
          </Typography>
          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Parameter</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><code>limit</code></TableCell>
                  <TableCell>Number of results to return (default: 10, max: 100)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>cursor</code></TableCell>
                  <TableCell>Opaque string from a previous response's <code>nextCursor</code> - pass it back to get the next page</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
          <Typography variant="body1">
            Responses include <code>hasMore</code> (boolean) and <code>nextCursor</code> (string, or <code>null</code> if there's no next page). There is no <code>total</code> count field - computing an exact total across a large collection on every request doesn't scale, so it isn't provided.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* GET /reports/data Endpoint (Granular Reports) */}
      <Accordion defaultExpanded id="granular-reports">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            GET /v1/reports/data <Chip label="reports:read" color="primary" size="small" sx={{ ml: 1 }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Retrieve granular report data (hazard/incident reports) from the Reports collection.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>Query Parameters</Typography>
          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Parameter</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Example</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell><code>limit</code></TableCell><TableCell>Results per page (default 10, max 100)</TableCell><TableCell><code>limit=10</code></TableCell></TableRow>
                <TableRow><TableCell><code>cursor</code></TableCell><TableCell>Pagination cursor from a previous response</TableCell><TableCell><code>cursor=eyJ0aW1l...</code></TableCell></TableRow>
                <TableRow><TableCell><code>reportType</code></TableCell><TableCell>Filter by report type</TableCell><TableCell><code>reportType=uxo</code></TableCell></TableRow>
                <TableRow><TableCell><code>country</code></TableCell><TableCell>Filter by country (free-text, matches the value as stored - e.g. "United States", not "USA")</TableCell><TableCell><code>country=United%20States</code></TableCell></TableRow>
                <TableRow><TableCell><code>hazardType</code></TableCell><TableCell>Filter by hazard type</TableCell><TableCell><code>hazardType=generalPurposeBomb</code></TableCell></TableRow>
                <TableRow><TableCell><code>bombType</code></TableCell><TableCell>Filter by bomb type</TableCell><TableCell><code>bombType=general_purpose_bomb_2</code></TableCell></TableRow>
                <TableRow><TableCell><code>verificationStatus</code></TableCell><TableCell>Filter by verification status</TableCell><TableCell><code>verificationStatus=verified</code></TableCell></TableRow>
                <TableRow><TableCell><code>priority</code></TableCell><TableCell>Filter by priority - <code>low</code>, <code>medium</code>, or <code>high</code></TableCell><TableCell><code>priority=high</code></TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>

          <TryIt apiKey={apiKey} buildUrl={buildGranularUrl} params={granularParams} onChange={setGranularParams} fields={[
            { name: 'limit', label: 'Limit', width: 80 },
            { name: 'reportType', label: 'Report Type', width: 160 },
            { name: 'country', label: 'Country', width: 140 },
            { name: 'priority', label: 'Priority (low/medium/high)', width: 200 },
          ]} />

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>Response Fields</Typography>
          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell><code>reports[]</code></TableCell><TableCell>Array of report objects</TableCell></TableRow>
                <TableRow><TableCell><code>reports[].priority</code></TableCell><TableCell>String - <code>low</code>, <code>medium</code>, or <code>high</code></TableCell></TableRow>
                <TableRow><TableCell><code>reports[].verificationStatus</code></TableCell><TableCell>e.g. "verified", "notStarted", "moreInfoNeeded"</TableCell></TableRow>
                <TableRow><TableCell><code>limit</code> / <code>hasMore</code> / <code>nextCursor</code></TableCell><TableCell>See Pagination above</TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>
        </AccordionDetails>
      </Accordion>

      {/* GET /reports/summary Endpoint */}
      <Accordion defaultExpanded id="aggregated-reports">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            GET /v1/reports/summary <Chip label="reports:read" color="primary" size="small" sx={{ ml: 1 }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Aggregated counts grouped by reportType, including a <code>priorityBreakdown</code> keyed by <code>low</code>/<code>medium</code>/<code>high</code>.
          </Typography>

          <TryIt apiKey={apiKey} buildUrl={buildSummaryUrl} params={summaryParams} onChange={() => {}} fields={[]} />

          <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>Example Response</Typography>
          <SyntaxHighlighter language="json" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px', marginTop: '8px' }}>
            {`{
  "aggregations": [
    {
      "reportType": "uxo",
      "count": 15,
      "verifiedCount": 15,
      "priorityBreakdown": { "low": 3, "medium": 7, "high": 5 },
      "hazardTypeBreakdown": { "null": 15 },
      "bombTypeBreakdown": { "null": 15 },
      "archived": false
    }
  ]
}`}
          </SyntaxHighlighter>
        </AccordionDetails>
      </Accordion>

      {/* GET /users/summary Endpoint */}
      <Accordion defaultExpanded id="aggregated-users">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            GET /v1/users/summary <Chip label="users:summary:read" color="primary" size="small" sx={{ ml: 1 }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Aggregated user stats grouped by a field (default: country), with optional time-range filtering on report activity.
          </Typography>

          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Parameter</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell><code>groupBy</code></TableCell><TableCell>reportType, country, hazardType, bombType, or archived (default: country)</TableCell></TableRow>
                <TableRow><TableCell><code>timeStart</code> / <code>timeEnd</code></TableCell><TableCell>ISO 8601 date range, optional</TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>

          <TryIt apiKey={apiKey} buildUrl={buildUsersUrl} params={usersParams} onChange={setUsersParams} fields={[
            { name: 'groupBy', label: 'Group By', width: 150 },
          ]} />
        </AccordionDetails>
      </Accordion>

      {/* GET /bolo-reports/namus Endpoint */}
      <Accordion defaultExpanded id="bolo-reports">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            GET /v1/bolo-reports/namus <Chip label="bolo:read" color="secondary" size="small" sx={{ ml: 1 }} />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Missing-persons (BOLO) reports imported from NamUs (the National Missing and Unidentified Persons System). A single-report variant is available at <code>/v1/bolo-reports/namus/:id</code>.
          </Typography>
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>Data coverage:</strong> this reflects NamUs cases we've been importing on an ongoing basis starting January 2026 - it is <strong>not</strong> a complete mirror of NamUs' full historical case archive, since older cases were never backfilled. New cases are added continuously. Use <code>createdAt</code> on each report to see when it was imported.
            </Typography>
          </Card>
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>Field visibility:</strong> name, approximate age/gender/ethnicity, physical description, city-level location, and incident narrative are included for any key with <code>bolo:read</code> - this matches how public missing-person alerts work, to help generate sightings. Direct contact info, exact last-known-location coordinates, and date of birth require the separate <code>bolo:read:pii</code> scope, which is granted manually per key and is not part of the standard request flow.
            </Typography>
          </Card>

          <Card sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Parameter</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell><code>limit</code></TableCell><TableCell>Results per page (default 10, max 100)</TableCell></TableRow>
                <TableRow><TableCell><code>cursor</code></TableCell><TableCell>Pagination cursor from a previous response</TableCell></TableRow>
                <TableRow><TableCell><code>status</code></TableCell><TableCell>Filter by status (e.g. <code>missing</code>)</TableCell></TableRow>
                <TableRow><TableCell><code>countryCode</code></TableCell><TableCell>Filter by ISO alpha-2 country code</TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>

          <TryIt apiKey={apiKey} buildUrl={buildBoloUrl} params={boloParams} onChange={setBoloParams} fields={[
            { name: 'limit', label: 'Limit', width: 80 },
            { name: 'countryCode', label: 'Country Code', width: 140 },
          ]} />
        </AccordionDetails>
      </Accordion>

      {/* Error Responses */}
      <Accordion defaultExpanded id="error-responses">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Error Responses</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" sx={{ mb: 2 }}>
            All errors share one consistent shape:
          </Typography>
          <SyntaxHighlighter language="json" style={vscDarkPlus} customStyle={{ padding: '16px', borderRadius: '8px', marginTop: '8px' }}>
            {`{
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Invalid query parameters",
    "details": { "priority": ["Invalid enum value..."] }
  }
}`}
          </SyntaxHighlighter>

          <Card sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>HTTP Status</strong></TableCell>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Meaning</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow><TableCell>400</TableCell><TableCell><code>VALIDATION_ERROR</code></TableCell><TableCell>Invalid query parameter(s)</TableCell></TableRow>
                <TableRow><TableCell>401</TableCell><TableCell><code>INVALID_API_KEY</code></TableCell><TableCell>Missing <code>X-API-Key</code> header</TableCell></TableRow>
                <TableRow><TableCell>403</TableCell><TableCell><code>INVALID_API_KEY</code></TableCell><TableCell>Key doesn't exist</TableCell></TableRow>
                <TableRow><TableCell>403</TableCell><TableCell><code>API_KEY_REVOKED</code></TableCell><TableCell>Key has been revoked</TableCell></TableRow>
                <TableRow><TableCell>403</TableCell><TableCell><code>API_ACCESS_PAUSED</code></TableCell><TableCell>Your account no longer has API access (plan below Professional, or account scheduled for deletion); the key resumes automatically once that changes</TableCell></TableRow>
                <TableRow><TableCell>403</TableCell><TableCell><code>FORBIDDEN_SCOPE</code></TableCell><TableCell>Key lacks the scope required for this endpoint</TableCell></TableRow>
                <TableRow><TableCell>404</TableCell><TableCell><code>NOT_FOUND</code></TableCell><TableCell>Resource doesn't exist</TableCell></TableRow>
                <TableRow><TableCell>429</TableCell><TableCell><code>RATE_LIMIT_EXCEEDED</code></TableCell><TableCell>Hourly limit exceeded - see <code>Retry-After</code> header</TableCell></TableRow>
                <TableRow><TableCell>429</TableCell><TableCell><code>QUOTA_EXCEEDED</code></TableCell><TableCell>Monthly quota exceeded</TableCell></TableRow>
                <TableRow><TableCell>500</TableCell><TableCell><code>INTERNAL_ERROR</code></TableCell><TableCell>Unexpected server error</TableCell></TableRow>
              </TableBody>
            </Table>
          </Card>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default ApiDocs;
